"use client";

import React from "react";
import { MapPin, Phone, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
const CustomInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input ref={ref} className={`w-full bg-background/40 border-b border-white/10 px-0 py-3 text-base outline-none transition-all placeholder:text-muted-foreground/30 focus:border-primary focus:ring-0 ${className}`} {...props} />;
});
CustomInput.displayName = "CustomInput";
const CustomTextarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea ref={ref} className={`w-full bg-background/40 border-b border-white/10 px-0 py-3 text-base outline-none transition-all min-h-[100px] resize-y placeholder:text-muted-foreground/30 focus:border-primary focus:ring-0 ${className}`} {...props} />;
});
CustomTextarea.displayName = "CustomTextarea";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="35901" className="h-full flex flex-col items-center justify-center p-8 bg-primary/10 border border-primary/20 rounded-lg text-center animate-in fade-in zoom-in duration-500">
        <h3 className="text-2xl font-sans font-bold text-foreground mb-3">Inquiry Received</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button variant="outline" className="mt-6 rounded-full border-primary/20 hover:bg-primary/10" onClick={() => window.location.reload()}>
          New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary/90 font-bold text-sm uppercase tracking-widest">Full Name</FormLabel>
            <FormControl>
              <CustomInput placeholder="Enter your name" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary/90 font-bold text-sm uppercase tracking-widest">Email Address</FormLabel>
            <FormControl>
              <CustomInput type="email" placeholder="your@email.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary/90 font-bold text-sm uppercase tracking-widest">Phone Number</FormLabel>
            <FormControl>
              <CustomInput type="tel" placeholder="0400 000 000" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Inquiry details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary/90 font-bold text-sm uppercase tracking-widest">How Can We Help?</FormLabel>
            <FormControl>
              <CustomTextarea placeholder="Tell us about your lawn or garden needs..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <div className="pt-6">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto rounded-full bg-primary text-primary-foreground hover:opacity-90 px-12 py-6 text-lg font-bold transition-all shadow-lg hover:shadow-[0_0_30px_rgba(34,197,94,0.4)]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Sending...
            </> : "Get My Free Quote"}
        </Button>
      </div>
    </Form>;
}
export default function ContactUs() {
  return <section id="project-consultation" className="dark bg-background text-foreground py-24 lg:py-32 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-white/5 to-transparent pointer-events-none opacity-50" />

      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
          
          {}
          <div className="lg:col-span-5 space-y-16 lg:sticky lg:top-32">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-sans font-extrabold text-foreground tracking-tighter leading-[1.1]">
                Project Consultation
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed max-w-md">
                Ready to elevate your property's curb appeal? Reach out today for a professional assessment and a free, no-obligation quote for your Central Coast home or business.
              </p>
            </div>

            <div className="space-y-12">
              <div className="group flex flex-col gap-4">
                <div className="flex items-center gap-3 text-primary">
                  <MapPin className="size-5" />
                  <span className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Service Area</span>
                </div>
                <h3 className="font-sans font-bold text-3xl md:text-4xl text-foreground">
                  Central Coast<br />New South Wales
                </h3>
              </div>

              <div className="group flex flex-col gap-4">
                <div className="flex items-center gap-3 text-primary">
                  <Phone className="size-5" />
                  <span className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Contact</span>
                </div>
                <h3 className="font-sans font-bold text-3xl md:text-4xl text-foreground">
                  0410 147 510<br />
                  <span className="text-[12px] text-muted-foreground">coastalprestigemowing@gmail.com</span>
                </h3>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 mt-8 lg:mt-0">
            <div className="bg-card/40 p-8 md:p-12 lg:p-16 rounded-2xl border border-white/10 backdrop-blur-md shadow-2xl">
              <FormProvider formId="contact_form_frankampshirleysrestaurant" sectionName="contact-us" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true
              }
            }} submitText="Get My Free Quote" successMessage="Thank you! We've received your inquiry and will be in touch shortly to discuss your property maintenance.">
                <ContactFormContent />
              </FormProvider>
            </div>
          </div>
          
        </div>
      </div>
    </section>;
}